import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import i18next from 'i18next';
import { Button } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type MoraConfig = { enabled: boolean; desdePeriodo: string; updatedAt: string | null; updatedBy: string | null };
type MoraRun = { id: number; origen: string; usuario: string | null; at: string; estado: string; resumen: string | null; error: string | null };
type MoraItem = {
  tenantId: string;
  clinica: string;
  periodo: string;
  total: number;
  moraActual: number;
  moraObjetivo: number;
  accion: 'aplicar' | 'aplicada' | 'nada';
  motivo: string | null;
  escalones: Array<{ pct: number; desde: string }>;
  error?: string;
};
type MoraResult = { fecha: string; simulacion: boolean; aplicadas: number; errores: number; items: MoraItem[]; resumen: string };

function apiMessage(err: unknown): string {
  const data = (err as { response?: { data?: { message?: unknown } } })?.response?.data;
  return typeof data?.message === 'string' && data.message.trim() ? data.message : i18next.t('common.error');
}

function todayAr(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'America/Argentina/Buenos_Aires' }).format(new Date());
}

const FIELD =
  'h-9 rounded-md border border-gray-300 bg-white px-2 text-sm text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white';

/** Mora automática (10 y 20 hábiles): activar, desde qué factura, simular con cualquier fecha y correr a mano. */
export function MoraJobSettings({ canEdit }: { canEdit: boolean }) {
  const { t, i18n } = useTranslation();
  const { success, error: showError } = useToast();
  const [config, setConfig] = useState<MoraConfig | null>(null);
  const [runs, setRuns] = useState<MoraRun[]>([]);
  const [enabled, setEnabled] = useState(false);
  const [desde, setDesde] = useState('2026-10');
  const [fecha, setFecha] = useState(todayAr());
  const [result, setResult] = useState<MoraResult | null>(null);
  const [busy, setBusy] = useState<'save' | 'simulate' | 'run' | null>(null);

  const load = useCallback(async () => {
    try {
      const res = await httpClient.get<{ config: MoraConfig; runs: MoraRun[] }>('/panel-config/recargos');
      setConfig(res.data.config);
      setRuns(res.data.runs);
      setEnabled(res.data.config.enabled);
      setDesde(res.data.config.desdePeriodo);
    } catch (err) {
      showError(apiMessage(err));
    }
  }, [showError]);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    if (enabled && !config?.enabled && !window.confirm(i18next.t('pages.settings.mora.enableConfirm', { desde }))) return;
    setBusy('save');
    try {
      await httpClient.put('/panel-config/recargos', { enabled, desdePeriodo: desde });
      success(i18next.t('pages.settings.mora.saved'));
      await load();
    } catch (err) {
      showError(apiMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const simulate = async () => {
    setBusy('simulate');
    try {
      const res = await httpClient.post<MoraResult>('/panel-config/recargos/simular', { fecha }, { timeout: 60000 });
      setResult(res.data);
    } catch (err) {
      showError(apiMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const runNow = async () => {
    if (!window.confirm(i18next.t('pages.settings.mora.runConfirm'))) return;
    setBusy('run');
    try {
      const res = await httpClient.post<MoraResult>('/panel-config/recargos/correr', {}, { timeout: 60000 });
      setResult(res.data);
      success(i18next.t('pages.settings.mora.ranOk', { count: res.data.aplicadas }));
      await load();
    } catch (err) {
      showError(apiMessage(err));
    } finally {
      setBusy(null);
    }
  };

  const money = (n: number) => n.toLocaleString(i18n.language, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const date = (iso: string) => {
    const [y, m, d] = iso.split('-').map(Number);
    return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString(i18n.language, { timeZone: 'UTC', day: 'numeric', month: 'short' });
  };
  const dirty = config ? enabled !== config.enabled || desde !== config.desdePeriodo : false;
  const shown = result ? result.items.filter((i) => i.accion !== 'nada' || i.motivo !== 'not_due') : [];

  return (
    <section className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
      <h2 className="text-base font-semibold text-slate-900 dark:text-white">{t('pages.settings.mora.title')}</h2>
      <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">{t('pages.settings.mora.subtitle')}</p>

      <div className="mt-4 flex flex-wrap items-end gap-4">
        <label className="flex items-center gap-2 text-sm text-slate-700 dark:text-slate-200">
          <input
            type="checkbox"
            className="h-4 w-4 rounded border-gray-300 text-teal-600 focus:ring-teal-500"
            checked={enabled}
            disabled={!canEdit || busy !== null}
            onChange={(e) => setEnabled(e.target.checked)}
          />
          {t('pages.settings.mora.enabled')}
        </label>
        <label className="text-sm text-slate-700 dark:text-slate-200">
          <span className="mb-1 block text-xs text-slate-500">{t('pages.settings.mora.fromPeriod')}</span>
          <input
            type="month"
            className={FIELD}
            value={desde}
            disabled={!canEdit || busy !== null}
            onChange={(e) => setDesde(e.target.value)}
          />
        </label>
        <Button type="button" size="sm" onClick={() => void save()} disabled={!canEdit || !dirty || busy !== null} isLoading={busy === 'save'}>
          {t('common.save')}
        </Button>
      </div>
      <p className="mt-2 text-xs text-slate-500 dark:text-slate-400">
        {config?.enabled ? t('pages.settings.mora.statusOn') : t('pages.settings.mora.statusOff')}
        {config?.updatedBy ? ` ${t('pages.settings.mora.updatedBy', { by: config.updatedBy, at: config.updatedAt ? new Date(config.updatedAt).toLocaleString(i18n.language) : '—' })}` : ''}
      </p>

      <div className="mt-5 border-t border-slate-100 pt-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('pages.settings.mora.simulateTitle')}</h3>
        <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">{t('pages.settings.mora.simulateHint')}</p>
        <div className="mt-3 flex flex-wrap items-end gap-2">
          <input type="date" className={FIELD} value={fecha} onChange={(e) => setFecha(e.target.value)} aria-label={t('pages.settings.mora.simulateDate')} />
          <Button type="button" size="sm" variant="secondary" onClick={() => void simulate()} disabled={busy !== null || !fecha} isLoading={busy === 'simulate'}>
            {t('pages.settings.mora.simulate')}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={() => void runNow()}
            disabled={!canEdit || !config?.enabled || busy !== null}
            isLoading={busy === 'run'}
            title={!config?.enabled ? t('pages.settings.mora.runDisabledHint') : undefined}
          >
            {t('pages.settings.mora.runNow')}
          </Button>
        </div>

        {result ? (
          <div className="mt-3">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {result.simulacion
                ? t('pages.settings.mora.simResult', {
                    date: date(result.fecha),
                    count: result.items.filter((i) => i.accion === 'aplicar').length,
                  })
                : t('pages.settings.mora.runResult', { count: result.aplicadas, errors: result.errores })}
            </p>
            {shown.length === 0 ? (
              <p className="mt-1 text-xs text-slate-500">{t('pages.settings.mora.nothing')}</p>
            ) : (
              <div className="mt-2 overflow-auto rounded-lg border border-slate-200 dark:border-gray-700">
                <table className="min-w-full text-xs">
                  <thead className="bg-slate-50 text-left uppercase tracking-wide text-slate-500 dark:bg-gray-900 dark:text-slate-400">
                    <tr>
                      <th className="px-2 py-1.5">{t('pages.settings.mora.colClinic')}</th>
                      <th className="px-2 py-1.5">{t('pages.settings.mora.colPeriod')}</th>
                      <th className="px-2 py-1.5 text-right">{t('pages.settings.mora.colTotal')}</th>
                      <th className="px-2 py-1.5">{t('pages.settings.mora.colMora')}</th>
                      <th className="px-2 py-1.5">{t('pages.settings.mora.colSteps')}</th>
                      <th className="px-2 py-1.5">{t('pages.settings.mora.colResult')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {shown.map((i) => (
                      <tr key={`${i.tenantId}-${i.periodo}`} className="border-t border-slate-100 dark:border-gray-700">
                        <td className="px-2 py-1.5 font-medium text-slate-900 dark:text-white">{i.clinica}</td>
                        <td className="px-2 py-1.5">{i.periodo}</td>
                        <td className="px-2 py-1.5 text-right tabular-nums">{money(i.total)}</td>
                        <td className="px-2 py-1.5 whitespace-nowrap">
                          {i.moraActual}% → {i.moraObjetivo}%
                        </td>
                        <td className="px-2 py-1.5 whitespace-nowrap text-slate-500">
                          {i.escalones.map((s) => `${s.pct}% ${t('pages.settings.mora.from')} ${date(s.desde)}`).join(' · ')}
                        </td>
                        <td className="px-2 py-1.5">
                          {i.error
                            ? <span className="text-rose-600">{i.error}</span>
                            : t(`pages.settings.mora.result.${i.accion === 'nada' ? i.motivo ?? 'already' : i.accion}`)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        ) : null}
      </div>

      <div className="mt-5 border-t border-slate-100 pt-4 dark:border-gray-700">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">{t('pages.settings.mora.runsTitle')}</h3>
        {runs.length === 0 ? (
          <p className="mt-1 text-xs text-slate-500">{t('pages.settings.mora.noRuns')}</p>
        ) : (
          <ul className="mt-2 space-y-1 text-xs text-slate-600 dark:text-slate-300">
            {runs.map((r) => (
              <li key={r.id}>
                <span className="text-slate-400">{new Date(r.at).toLocaleString(i18n.language)}</span>{' '}
                · {r.origen === 'auto' ? t('pages.settings.mora.originAuto') : t('pages.settings.mora.originManual', { by: r.usuario ?? '—' })} ·{' '}
                <span className={r.estado === 'error' ? 'text-rose-600' : ''}>{r.resumen ?? r.error ?? '—'}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
